/**
 * Host app light/dark theme helpers.
 * Persists to localStorage and applies Blueprint's bp5-dark class on
 * document.body and #root (so panel text inherits correctly under Dockview).
 */

export const THEME_KEY = 'bodapro-theme';
export const THEME_CHANGED_COMMAND = 'bodapro.theme.changed';

/**
 * @returns {'dark' | 'light'}
 */
export function getStoredTheme() {
    try {
        const stored = localStorage.getItem(THEME_KEY);
        if (stored === 'light' || stored === 'dark') {
            return stored;
        }
    } catch (_) {
        // ignore storage errors
    }
    return 'dark';
}

/**
 * Persist theme, update globalThis, and toggle Blueprint dark class on body + #root.
 * @param {'dark' | 'light'} theme
 * @param {{ persist?: boolean }} [options] — when persist is false, skip localStorage (e.g. temporary login light mode)
 * @returns {'dark' | 'light'}
 */
export function applyTheme(theme, options = {}) {
    const next = theme === 'light' ? 'light' : 'dark';
    const persist = options.persist !== false;
    if (persist) {
        try {
            localStorage.setItem(THEME_KEY, next);
        } catch (_) {
            // ignore storage errors
        }
    }
    globalThis.bodaproTheme = next;
    if (typeof document !== 'undefined') {
        const isDark = next === 'dark';
        if (document.body) {
            document.body.classList.toggle('bp5-dark', isDark);
        }
        document.getElementById('root')?.classList.toggle('bp5-dark', isDark);
    }
    return next;
}

/**
 * @returns {'dark' | 'light'}
 */
export function getTheme() {
    return globalThis.bodaproTheme || getStoredTheme();
}

/** Near-white — readable on dark activity bar / tabs */
export const ICON_COLOR_DARK = '#f5f8fa';
/** Blueprint secondary gray — readable on light activity bar / tabs */
export const ICON_COLOR_LIGHT = '#5c7080';

/**
 * @param {'dark' | 'light'} [theme]
 * @returns {string}
 */
export function getIconThemeColor(theme = getTheme()) {
    return theme === 'light' ? ICON_COLOR_LIGHT : ICON_COLOR_DARK;
}

/**
 * Style an injected extension SVG so fill- and stroke-based icons follow host theme.
 * @param {SVGElement} svg
 * @param {{ size?: number, theme?: 'dark' | 'light' }} [options]
 */
export function applySvgIconTheme(svg, options = {}) {
    const theme = options.theme === 'light' || options.theme === 'dark' ? options.theme : getTheme();
    const size = options.size || 30;
    const isDark = theme === 'dark';
    svg.classList.toggle('bp5-dark', isDark);
    svg.setAttribute(
        'style',
        `width: ${size}px; height: ${size}px; fill: currentColor; stroke: currentColor; color: ${getIconThemeColor(theme)};`
    );
}
